import React from "react";
import { useLocation } from "react-router-dom";
import { Col, Container, Label, Row } from "reactstrap";

import { UserStatus } from "../../../constants";
import { CustomText } from "../../../components/CustomText";
import SubscriptionStatus from "../../../constants/SubscriptionStatus";
import { GetAudioById } from "../../../api/api.service";
import { useRequestProcessor } from "../../../hooks/useRequestProcessors";
import { getKey, MODULE_KEYS } from "../../../api/api.keys";
import { hasData, truncateFileName } from "../../../utils/commonUtils";
import { LoaderFallbackUI } from "../../../components/LoaderFallbackUI";
import APP_CONFIG from "../../../APP_CONFIG";

const AudioSeriesDetail = () => {
    const location = useLocation();

    // Extract the ID from the path (assuming the path is "/audio/singles/details/21")
    const seriesID = location?.pathname?.split("/")?.pop();

    let { useFetch } = useRequestProcessor();

    const { data, isLoading, isFetching } = useFetch(
        getKey([MODULE_KEYS.AUDIO_BY_ID], { seriesID }),
        async ({ signal }) =>
            await GetAudioById({
                Id: seriesID
            })
    );
    const thumbnail = data?.thumbnail;
    const croppedThumbnail = data?.croppedThumbnail;
    const audio = data?.audios?.[0]?.audioMedia;

    if (isFetching || isLoading || !hasData(data)) {
        return (
            <div style={{ height: "70vh" }}>
                <LoaderFallbackUI isLoader={isFetching} className={"h-100"} />
            </div>
        );
    }

    return (
        <Container fluid className="py-4 audio-single-detail-container">
            <Row className="mb-4">
                <Col md="3">
                    <CustomText
                        title={"Series Title"}
                        description={data?.title || "-"}
                        titleClass="label-classes"
                        descClass="desc-classes"
                    />
                </Col>
                <Col md="3">
                    <CustomText
                        title="Sub Category"
                        description={
                            data?.albumLabels?.length
                                ? data.albumLabels
                                      .map((item) => item.label?.title)
                                      .filter(Boolean)
                                      .join(", ")
                                : "-"
                        }
                        titleClass="label-classes"
                        descClass="desc-classes"
                    />
                </Col>
                <Col md="3">
                    <CustomText
                        titleClass="label-classes"
                        descClass="desc-classes"
                        description={data?.author?.name || "-"}
                        title={"Author"}
                    />
                </Col>
                <Col md="3">
                    <CustomText
                        title={"Narrator"}
                        description={data?.narrator?.name || "-"}
                        titleClass="label-classes"
                        descClass="desc-classes"
                    />
                </Col>
            </Row>
            <Row className="mb-4">
                <Col md="3">
                    <CustomText
                        titleClass="label-classes"
                        descClass={`  ${
                            data?.status ? "status-common-class" : ""
                        } ${UserStatus.getLabelClass(data?.status)}`}
                        title={"Status"}
                        description={UserStatus.getDisplayTextKey(data?.status) || "N/A"}
                    />
                </Col>
            </Row>

            <Row className="my-2">
                <Col md="12">
                    <CustomText
                        titleClass="label-classes"
                        description={data?.audios?.[0]?.description || "-"}
                        title={"Series Description"}
                        descClass="desc-classes"
                    />
                </Col>
            </Row>

            <Row className="px-4">
                <Col md={6}>
                    <p className="mb-2 input-label">{"Series Thumbnail"}</p>
                    <div className="uploaded-file-container ">
                        <div className="file-preview">
                            <img
                                src={
                                    `${croppedThumbnail.location}${croppedThumbnail.thumbPath}` ||
                                    APP_CONFIG.IMAGES.AVATAR_PLACEHOLDER
                                }
                                alt={croppedThumbnail.name || "Thumbnail"}
                                className="uploaded-thumbnail"
                            />
                        </div>

                        <div className="file-details">
                            <span className="file-name">
                                {truncateFileName(croppedThumbnail?.name, 20) || "-"}
                            </span>
                            <span className="file-size">
                                {`${croppedThumbnail?.size} KB` || "-"}
                            </span>{" "}
                        </div>
                    </div>
                </Col>
                <Col md={6}>
                    <p className="mb-2 input-label">{"Series Uploaded Image"}</p>
                    <div className="uploaded-file-container ">
                        <div className="file-preview">
                            <img
                                src={
                                    `${thumbnail.location}${thumbnail.thumbPath}` ||
                                    APP_CONFIG.IMAGES.AVATAR_PLACEHOLDER
                                }
                                alt={thumbnail.name || "Thumbnail"}
                                className="uploaded-thumbnail"
                            />
                        </div>

                        <div className="file-details">
                            <span className="file-name">
                                {truncateFileName(thumbnail?.name, 10) || "-"}
                            </span>
                            <span className="file-size">{`${thumbnail?.size} KB` || "-"}</span>{" "}
                        </div>
                    </div>
                </Col>
            </Row>

            <Row className=" my-4">
                <Col md={12}>
                    <div className="audio-session-container py-2 px-4">
                        <div className="session-heading">Session Details</div>
                        {data?.audios?.map((session, index) => (
                            <div key={index} className="session-detail-container my-4">
                                <Row>
                                    <Col md={6}>
                                        <CustomText
                                            titleClass={`status-common-class mb-2 ${SubscriptionStatus.getLabelClass(
                                                session?.tierType
                                            )}`}
                                            description={session?.title || "N/A"}
                                            title={SubscriptionStatus.getDisplayTextKey(
                                                session?.tierType
                                            )}
                                            descClass="session-label-classes"
                                        />
                                    </Col>

                                    <Col md={6}>
                                        <div className="uploaded-audio-sessions-container">
                                            <div className="file-preview ">
                                                <audio controls className="uploaded-audio">
                                                    <source
                                                        src={`${session?.audioMedia?.location}${session?.audioMedia?.thumbPath}`}
                                                        type={`audio/${audio.extension || "mpeg"}`}
                                                    />
                                                </audio>
                                            </div>
                                            <div className="file-details">
                                                <span className="file-name">
                                                    {truncateFileName(
                                                        session?.audioMedia?.name,
                                                        10
                                                    ) || "-"}
                                                </span>
                                                <span className="file-size">
                                                    {`${session?.audioMedia?.size} KB` || "-"}
                                                </span>{" "}
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                                <Row className="my-4">
                                    <Col md="12">
                                        <CustomText
                                            titleClass="label-classes mb-2"
                                            description={session?.description || "-"}
                                            title={"Session Description"}
                                            descClass="desc-classes"
                                        />
                                    </Col>
                                </Row>

                                <Row className="my-4">
                                    <Col md="12">
                                        <CustomText
                                            titleClass="label-classes mb-2"
                                            description={session?.completionMessage || "-"}
                                            title={"Session Success Message"}
                                            descClass="desc-classes"
                                        />
                                    </Col>
                                </Row>
                            </div>
                        ))}
                    </div>
                </Col>
            </Row>
        </Container>
    );
};

export default AudioSeriesDetail;
